import React from 'react';
import Card from '../Card';
import SCurveChart from '../SCurveChart';
import {ProjectStatusChart} from '../Charts';
import CriticalPathCard from '../CriticalPathCard';
import FinanceSummaryCharts from '../FinanceSummaryCharts';
import PhysicalProgressChart from '../../physicalProgressChart/PhysicalProgressChart';
import ProjectStatusTable from './ProjectStatusTable';
import UpcomingMilestones from '../UpcomingMilestones';
import { Eye, Maximize2 } from 'lucide-react';

const DashboardCharts = ({
  visibleCharts,
  sCurveData,
  sCurveLoading,
  selectedProject,
  selectedShip,
  selectedYear,
  setFullScreenChart,
  setSelectedChartType,
  setSelectedChartData,
  setShowChartDetails,
  getChartData
}) => {
  const ChartCard = ({
    title,
    chartId,
    children,
    noDataMessage = 'No data available',
    className = '',
    hideViewDetails = false  // New prop to control visibility
  }) => {
    return (
      <Card
        title={title}
        className={`mb-6 ${className}`}
        headerRight={
          <div className="flex items-center space-x-2">
            {/* Conditionally render View Details button */}
            {!hideViewDetails && (
              <button
                onClick={() => {
                  setSelectedChartType(chartId);
                  setSelectedChartData(getChartData(chartId));
                  setShowChartDetails(true);
                }}
                className="text-xs sm:text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center"
              >
                <Eye size={14} className="mr-1 sm:mr-1" />
                <span className="hidden sm:inline">View Details</span>
                <span className="sm:hidden">Details</span>
              </button>
            )}
            <button
              onClick={() => setFullScreenChart(chartId)}
              className="p-1 rounded hover:bg-gray-100"
            >
              <Maximize2 size={16} className="text-gray-500 sm:w-[18px] sm:h-[18px]" />
            </button>
          </div>
        }
      >
        {children || (
          <div className="flex items-center justify-center h-48 sm:h-64 text-gray-500 text-sm sm:text-base">
            {noDataMessage}
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-4 sm:gap-6">
      {visibleCharts.includes('sCurve') && (
        <ChartCard
          title="Project Progress (S-Curve)"
          chartId="sCurve"
          noDataMessage={selectedProject ? 'No S-Curve data available' : 'Select a project to view S-Curve'}
        >
          {sCurveLoading ? (
            <div className="flex items-center justify-center h-48 sm:h-64">
              <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
          ) : sCurveData.length > 0 && sCurveData.some(d => (d.contractual ?? 0) !== 0 || (d.revised ?? 0) !== 0 || (d.achieved ?? 0) !== 0) ? (
            <SCurveChart
              data={sCurveData}
              selectedYear={selectedYear}
              selectedProject={selectedProject}
              selectedShip={selectedShip}
            />
          ) : null}
        </ChartCard>
      )}

      {visibleCharts.includes('projectStatus') && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
          {/* Project Status Chart - Added hideViewDetails prop */}
          <div className="w-full">
            <ChartCard
              title="Project Status"
              chartId="projectStatus"
              noDataMessage={selectedProject ? 'No Project Status data available' : 'Select a project to view Project Status'}
              className="h-full"
              hideViewDetails={true}  // This hides the button
            >
              <div className="h-full">
                <ProjectStatusChart
                  selectedProject={selectedProject}
                  selectedYear={selectedYear}
                  selectedShip={selectedShip}
                />
              </div>
            </ChartCard>
          </div>

          {/* Project Status Details */}
          <div className="w-full">
            <Card
              title="Project Status Details"
              className="h-full mb-6"
            >
              <div className="h-full overflow-hidden">
                <ProjectStatusTable selectedShip={selectedShip} />
              </div>
            </Card>
          </div>
        </div>
      )}

      {visibleCharts.includes('criticalPath') && (
        <ChartCard
          title="Critical Path Analysis"
          chartId="criticalPath"
          hideViewDetails={true} // Add this line to hide the "View Details" button
        >
          <CriticalPathCard
            selectedProject={selectedProject}
            selectedShip={selectedShip}
          />
        </ChartCard>
      )}

      {visibleCharts.includes('financialOverview') && (
        <ChartCard
          title="Yearly Financial Overview"
          chartId="financialOverview"
        >
          <FinanceSummaryCharts
            selectedProject={selectedProject}
            selectedShip={selectedShip}
          />
        </ChartCard>
      )}

      {visibleCharts.includes('physicalProgress') && (
        <ChartCard
          title="Physical Progress Charts"
          chartId="physicalProgress"
        >
          <PhysicalProgressChart
            projectId={selectedShip}
            selectedYear={selectedYear}
          />
        </ChartCard>
      )}

      <UpcomingMilestones
        selectedProject={selectedProject}
        selectedShip={selectedShip}
      />
    </div>
  );
};

export default DashboardCharts;